import { isValid } from 'date-fns';

import { type Address } from '@electricjs/arc';

import {
  Asset as AssetDto,
  AssetLog as AssetLogDto,
  AssetLogWithChildren as AssetLogWithChildrenDto,
  CreateAssetsRequest as CreateAssetsRequestDto,
  CreateAssetsResponse as CreateAssetsResponseDto,
  SiteCreateRequest as SiteCreateRequestDto,
  SiteWithAddress as SiteWithAddressDto,
  Location as LocationDto,
  AddFilesResponse as AddFilesResponseDto,
  PaginatedResponseAssetFileWithURL as PaginatedAssetFileWithURLDto,
  AssetFileWithURL as AssetFileWithURLDto,
  WarehouseMetrics as WarehouseMetricsDto,
  AssetEventsResponse as AssetEventsResponseDto,
  BulkAssetsResponse as BulkAssetsResponseDto,
  StorefrontAsset as StorefrontAssetDto,
  StorefrontAssetsPaginatedResponse as StorefrontAssetsPaginatedResponseDto,
  ShipmentAsset as ShipmentAssetDto,
  ShipmentAssetEmployee as ShipmentAssetEmployeeDto,
  ShipmentAssetEmployeeAddress as ShipmentAssetEmployeeAddressDto,
  DirectoryInsightEventType,
  ProductTypeName,
  SortOrder,
} from '@electricjs/core_entity-client';
import { ApiListResponseDto } from '@externalTypes/general';

import {
  invalidatesListAndById,
  providesListAndAllById,
} from './helpers/cacheTagHelpers';
import { DEVICE_PRODUCT_TYPES } from '@/constants/devices';
import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  AddFileToAssetArgs,
  Asset,
  AssetHistory,
  AssetHistoryArgs,
  AssetHistoryResponse,
  AssetHistoryWithChildrenResponse,
  AssetListArgs,
  AssetUpdateArgs,
  AssetUpdateRequestDto,
  AssetsCreationArgs,
  AssetFileWithURL,
  BulkAssetsArgs,
  BulkAssetsResponse,
  EligibleForShipmentAssetListArgs,
  IgnoreAssetLogArgs,
  Location,
  LocationCreationArgs,
  OrganizationAssetHistoryArgs,
  SiteCreationArgs,
  SiteListArgs,
  SiteWithAddress,
  WarehouseMetrics,
  AssetEvent,
  AssetEventsHistoryArgs,
  AssetEventsHistoryResponse,
  GeneratePresignedUploadUrlsArgs,
  PresignedUploadUrlResponse,
  PresignedUploadUrlRawResponse,
  StorefrontAsset,
  AssetStorefrontListArgs,
  AssetStorefrontListResponse,
  AssetStorefrontDetailArgs,
  ShipmentEligibleAsset,
  ShipmentEligibleAssetEmployee,
} from '@/types/assets';
import { QueryParam } from '@/types/queryParamKeys';
import { DynamicFilterKey } from '@/types/storefront';

import { camelize } from '@/redux/slices/helpers/responseCamelizer';

const parseAssetLogResponse = (data: AssetLogDto) => {
  return {
    id: data.id,
    assetId: data.asset_id,
    action: data.action,
    data: data?.data,
    description: data.description,
    performedBy: data.performed_by,
    performedByUser: data.performed_by,
    performedAt: data.performed_at,
    parentAssetLogId: data.parent_asset_log_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  } as AssetHistory;
};

export const assetApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    //
    // Assets
    //
    // Returns the created assets so callers can immediately use their
    // server-assigned IDs (e.g. selecting a just-created asset in a dropdown)
    // without an extra round-trip fetch.
    createAssets: builder.mutation<
      { createdAssets: Asset[] },
      AssetsCreationArgs
    >({
      query: ({ organizationId, assets }) => {
        const body: CreateAssetsRequestDto = {
          assets: assets.map(asset => ({
            name: asset.name,
            product_type_id: asset.productTypeId,
            custom_asset_id: asset.customAssetId,
            status: asset.status,
            purchased_from_url: asset.purchasedFromUrl,
            brand: asset.brand,
            model: asset.model,
            serial_number: asset.serialNumber,
            notes: asset.notes,
            internal_notes: asset.internalNotes,
            image_url: asset.imageUrl,
            site_id: asset.siteId === '' ? undefined : asset.siteId,
            location_id: asset.locationId,
            condition: asset.condition,
            purchase_date: asset.purchaseDate?.toISOString(),
            purchase_price: asset.purchasePrice ?? undefined,
            purchase_price_currency_code: asset.purchasePriceCurrencyCode,
            assigned_to: asset.assignedTo === '' ? undefined : asset.assignedTo,
            warranty_expiration_date:
              asset.warrantyExpirationDate?.toISOString(),
          })),
        };
        return {
          url: `/v1/organizations/${organizationId}/assets`,
          method: 'POST',
          body: body,
        };
      },
      transformResponse: (
        response: CreateAssetsResponseDto
      ): { createdAssets: Asset[] } => ({
        createdAssets: (response.created_assets ?? []).map(asset =>
          camelize(asset)
        ),
      }),
      invalidatesTags: [TagTypes.Asset],
    }),
    listAssetsV2: builder.query<ApiListResponseDto<Asset>, AssetListArgs>({
      providesTags: [TagTypes.Asset],
      query: ({
        organizationId,
        limit,
        offset,
        orderBy,
        sortOrder,
        searchTerm,
        statuses,
        productTypes,
        hasAssignee,
        isDevice,
        warrantyStatus,
        assignedTo,
        hasMdmEntitledAssignee,
        mdmStatus,
        includeMdmStatus,
        healthStatus,
        eolStatus,
      }) => {
        //map each product type as a query param
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        const statusesQueryParams: QueryParam[] =
          statuses?.map(status => ({
            key: 'statuses',
            value: status,
          })) ?? [];

        const warrantyStatusQueryParams: QueryParam[] =
          warrantyStatus?.map(warrantyStatus => ({
            key: 'warranty_status',
            value: warrantyStatus,
          })) ?? [];

        const mdmStatusQueryParams: QueryParam[] =
          mdmStatus?.map(mdmStatus => ({
            key: 'mdm_status',
            value: mdmStatus,
          })) ?? [];

        const includeMdmStatusParam: QueryParam[] =
          includeMdmStatus === true
            ? [{ key: 'include_mdm_status', value: 'true' }]
            : [];

        const healthStatusQueryParams: QueryParam[] =
          healthStatus?.map(healthStatus => ({
            key: 'health_status',
            value: healthStatus,
          })) ?? [];

        const eolStatusQueryParams: QueryParam[] =
          eolStatus?.map(eolStatus => ({
            key: 'os_eol_status',
            value: eolStatus,
          })) ?? [];

        // isDevice filter (Assets page): Yes = all device types (laptop, desktop, phone, tablet), No = non-devices
        const getDeviceQueryParams = () => {
          if (isDevice === true) {
            return DEVICE_PRODUCT_TYPES.map(productType => ({
              key: DynamicFilterKey.TYPE,
              value: String(productType),
            }));
          } else if (isDevice === false) {
            const allProductTypesExceptDevices = Object.values(
              ProductTypeName
            ).filter(
              productType => !DEVICE_PRODUCT_TYPES.includes(productType)
            );
            return allProductTypesExceptDevices.map(productType => ({
              key: DynamicFilterKey.TYPE,
              value: String(productType),
            }));
          }

          return [];
        };

        const deviceQueryParams: QueryParam[] = getDeviceQueryParams();

        // composing the final query params
        const queryParams: QueryParam[] = [
          ...(offset !== undefined && offset !== null
            ? [{ key: 'offset', value: String(offset) }]
            : []),
          ...(limit !== undefined && limit !== null
            ? [{ key: 'limit', value: String(limit) }]
            : []),
          { key: 'order_by', value: orderBy ?? 'name' },
          { key: 'sort_order', value: sortOrder ?? 'asc' },
          { key: 'search_term', value: searchTerm },
          ...(hasAssignee !== undefined
            ? [
                {
                  key: 'has_assignee',
                  value: String(hasAssignee),
                },
              ]
            : []),
          ...(assignedTo
            ? [
                {
                  key: 'assigned_to',
                  value: assignedTo,
                },
              ]
            : []),
          ...(hasMdmEntitledAssignee !== undefined
            ? [
                {
                  key: 'has_mdm_entitled_assignee',
                  value: String(hasMdmEntitledAssignee),
                },
              ]
            : []),
          ...productTypesQueryParams,
          ...statusesQueryParams,
          ...warrantyStatusQueryParams,
          ...deviceQueryParams,
          ...mdmStatusQueryParams,
          ...includeMdmStatusParam,
          ...healthStatusQueryParams,
          ...eolStatusQueryParams,
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          // manually passing query string in order to handle repetitive params (backend doesn't support array of values)
          url: `/v1/organizations/${organizationId}/assets?${queryString}`,
        };
      },
      transformResponse: (
        response: ApiListResponseDto<AssetDto>
      ): ApiListResponseDto<Asset> => camelize(response),
    }),
    searchAssetsEligibleForShipment: builder.query<
      ApiListResponseDto<ShipmentEligibleAsset>,
      EligibleForShipmentAssetListArgs
    >({
      providesTags: [TagTypes.Asset],
      query: ({ organizationId, searchTerm, limit, offset }) => {
        // composing the final query params
        const queryParams: QueryParam[] = [
          ...(offset !== undefined && offset !== null
            ? [{ key: 'offset', value: String(offset) }]
            : []),
          ...(limit !== undefined && limit !== null
            ? [{ key: 'limit', value: String(limit) }]
            : []),
          { key: 'search_term', value: searchTerm },
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          url: `/v1/organizations/${organizationId}/assets/eligible-for-shipment?${queryString}`,
        };
      },
      transformResponse: (
        response: ApiListResponseDto<ShipmentAssetDto>
      ): ApiListResponseDto<ShipmentEligibleAsset> => ({
        total: response.total,
        results: response.results.map(transformShipmentEligibleAsset),
      }),
    }),
    getAsset: builder.query<Asset, { organizationId: string; assetId: string }>(
      {
        query: ({ organizationId, assetId }) => {
          return {
            url: `/v1/organizations/${organizationId}/assets/${assetId}`,
            params: { include_mdm_status: true },
          };
        },
        transformResponse: (response: AssetDto) => camelize(response),
        providesTags: [TagTypes.Asset, TagTypes.AssetDetail],
      }
    ),
    listAssetsStorefront: builder.query<
      AssetStorefrontListResponse,
      AssetStorefrontListArgs
    >({
      providesTags: [TagTypes.Asset],
      query: ({
        organizationId,
        shoppingCountryCode,
        limit,
        offset,
        productTypes,
        shippableOnly,
      }) => {
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        // composing the final query params
        const queryParams: QueryParam[] = [
          { key: 'shopping_country_code', value: shoppingCountryCode },
          ...(offset !== undefined && offset !== null
            ? [{ key: 'offset', value: String(offset) }]
            : []),
          ...(limit !== undefined && limit !== null
            ? [{ key: 'limit', value: String(limit) }]
            : []),
          ...(shippableOnly !== undefined
            ? [{ key: 'shippable_only', value: String(shippableOnly) }]
            : []),
          ...productTypesQueryParams,
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          // manually passing query string in order to handle repetitive params (backend doesn't support array of values)
          url: `/v1/organizations/${organizationId}/assets/storefront?${queryString}`,
        };
      },
      transformResponse: (
        response: StorefrontAssetsPaginatedResponseDto
      ): AssetStorefrontListResponse => ({
        total: response.total,
        totalNonShippable: response.total_non_shippable,
        results: response.results.map(transformStorefrontAsset),
      }),
    }),
    getAssetStorefront: builder.query<
      StorefrontAsset,
      AssetStorefrontDetailArgs
    >({
      query: ({ organizationId, assetId, shoppingCountryCode }) => ({
        url: `/v1/organizations/${organizationId}/assets/storefront/${assetId}`,
        params: { shopping_country_code: shoppingCountryCode },
      }),
      transformResponse: (response: StorefrontAssetDto): StorefrontAsset =>
        transformStorefrontAsset(response),
      providesTags: [TagTypes.Asset, TagTypes.AssetDetail],
    }),
    getBulkAssets: builder.query<BulkAssetsResponse, BulkAssetsArgs>({
      query: ({ organizationId, assetIds }) => ({
        url: `/v1/organizations/${organizationId}/assets/bulk`,
        method: 'POST',
        body: {
          asset_ids: assetIds,
        },
      }),
      transformResponse: (response: BulkAssetsResponseDto) => {
        const assetsMapping: Record<string, Asset> = {};
        Object.entries(response.assets_mapping).forEach(
          ([assetId, assetDto]) => {
            assetsMapping[assetId] = camelize(assetDto);
          }
        );
        return {
          assetsMapping,
        };
      },
      providesTags: (_result, _error, { assetIds }) => [
        { type: TagTypes.Asset, id: 'LIST' },
        ...assetIds.map(assetId => ({
          type: TagTypes.Asset as const,
          id: assetId,
        })),
      ],
    }),
    updateAsset: builder.mutation<void, AssetUpdateArgs>({
      query: ({ organizationId, assetId, ...asset }) => {
        const body: AssetUpdateRequestDto = {
          name: asset.name,
          product_type_id: asset.productTypeId,
          custom_asset_id: asset.customAssetId,
          assigned_to: asset.assignedTo === '' ? undefined : asset.assignedTo,
          purchase_date: asset.purchaseDate?.toISOString(),
          purchase_price: asset.purchasePrice,
          purchase_price_currency_code: asset.purchasePriceCurrencyCode,
          purchased_from_url: asset.purchasedFromUrl,
          brand: asset.brand,
          model: asset.model,
          serial_number: asset.serialNumber,
          notes: asset.notes,
          internal_notes: asset.internalNotes,
          site_id: asset.siteId === '' ? undefined : asset.siteId,
          condition: asset.condition,
          image_url: asset.imageUrl,
          status: asset.status,
          warranty_expiration_date: transformDate(asset.warrantyExpirationDate),
        };
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}`,
          method: 'PATCH',
          body: body,
        };
      },
      invalidatesTags: [TagTypes.Asset, TagTypes.AssetDetail],
    }),
    updateAssetLocation: builder.mutation<
      void,
      { organizationId: string; assetId: string; locationId: string | null }
    >({
      query: ({ organizationId, assetId, locationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/location`,
          method: 'PATCH',
          body: {
            location_id: locationId,
          },
        };
      },
      invalidatesTags: [TagTypes.Asset, TagTypes.AssetDetail],
    }),
    getAssetHistory: builder.query<AssetHistoryResponse, AssetHistoryArgs>({
      query: arg => {
        return {
          params: {
            limit: arg?.limit ?? 10,
            offset: arg?.offset ?? 0,
          },
          url: `/v1/organizations/${arg.organizationId}/assets/${arg.assetId}/asset_history`,
        };
      },
      transformResponse: (response: ApiListResponseDto<AssetLogDto>) => {
        const body = response.results.map(assetLog => {
          return {
            id: assetLog.id,
            assetId: assetLog.asset_id,
            action: assetLog.action,
            data: assetLog?.data,
            description: assetLog.description,
            performedBy: assetLog.performed_by,
            performedByUser: assetLog.performed_by,
            performedAt: assetLog.performed_at,
            parentAssetLogId: assetLog.parent_asset_log_id,
            createdAt: assetLog.created_at,
            updatedAt: assetLog.updated_at,
          } as AssetHistory;
        });

        return {
          total: response.total ?? 0,
          assetHistory: body,
        };
      },
      providesTags: [TagTypes.Asset],
    }),
    getOrganizationAssetHistory: builder.query<
      AssetHistoryResponse,
      OrganizationAssetHistoryArgs
    >({
      query: ({
        organizationId,
        assetActions,
        ignoreStatuses,
        days,
        limit,
        offset,
        ignoreDaysForUnresolvedIssues,
      }) => {
        const params = new URLSearchParams();

        limit && params.append('limit', String(limit));
        offset && params.append('offset', String(offset));
        days && params.append('days', String(days));
        ignoreDaysForUnresolvedIssues &&
          params.append('ignore_days_for_unresolved_issues', 'true');

        // Adding the list parameter
        assetActions &&
          assetActions.forEach(action =>
            params.append('asset_actions', action)
          );
        ignoreStatuses &&
          ignoreStatuses.forEach(status =>
            params.append('ignore_statuses', status)
          );

        return {
          url: `/v1/organizations/${organizationId}/assets/history?${params.toString()}`,
        };
      },
      transformResponse: (response: ApiListResponseDto<AssetLogDto>) => {
        const body = response.results.map(assetLog => {
          return parseAssetLogResponse(assetLog);
        });
        return {
          total: response.total ?? 0,
          assetHistory: body,
        };
      },
      providesTags: result =>
        providesListAndAllById(TagTypes.AssetLog)(result?.assetHistory),
    }),
    getOrganizationAssetHistoryUnresolved: builder.query<
      AssetHistoryWithChildrenResponse,
      Pick<OrganizationAssetHistoryArgs, 'organizationId'>
    >({
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/devices/issues/unresolved`,
        };
      },
      transformResponse: (response: AssetLogWithChildrenDto[]) => {
        const body = response.map(assetLog => {
          return {
            ...parseAssetLogResponse(assetLog),
            children: (assetLog.children ?? []).map(children =>
              parseAssetLogResponse(children)
            ),
          };
        });
        return {
          total: response.length ?? 0,
          assetHistory: body,
        };
      },
      providesTags: result =>
        providesListAndAllById(TagTypes.AssetLog)(result?.assetHistory),
    }),
    ignoreAssetLog: builder.mutation<void, IgnoreAssetLogArgs>({
      query: ({ organizationId, assetLogId }) => {
        return {
          url: `/v1/organizations/${organizationId}/devices/issues/ignore/${assetLogId}`,
          method: 'POST',
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(TagTypes.AssetLog, args.assetLogId),
      ],
    }),
    getMyAssets: builder.query<
      ApiListResponseDto<Asset>,
      { organizationId: string }
    >({
      providesTags: [TagTypes.Asset],
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/me/assets`,
        };
      },
      transformResponse: (
        response: ApiListResponseDto<AssetDto>
      ): ApiListResponseDto<Asset> => camelize(response),
    }),
    getEmployeeAssets: builder.query<
      ApiListResponseDto<Asset>,
      { organizationId: string; employeeId: string }
    >({
      providesTags: [TagTypes.Asset],
      query: ({ organizationId, employeeId }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/assets`,
        };
      },
      transformResponse: (
        response: ApiListResponseDto<AssetDto>
      ): ApiListResponseDto<Asset> => camelize(response),
    }),
    deleteAsset: builder.mutation<
      void,
      { organizationId: string; assetId: string }
    >({
      query: ({ organizationId, assetId }) => ({
        url: `/v1/organizations/${organizationId}/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Asset],
    }),
    //
    // Sites
    //
    createSite: builder.mutation<SiteWithAddress, SiteCreationArgs>({
      query: ({ organizationId, ...site }) => {
        const body: SiteCreateRequestDto = {
          name: site.name,
          street_1: site.address.streetAddress1,
          street_2: site.address.streetAddress2 ?? undefined,
          city: site.address.city,
          state: site.address.state,
          zip_code: site.address.zip,
          country: site.address.country,
          phone_number: site.phone ?? undefined,
        };
        return {
          url: `/v1/organizations/${organizationId}/sites`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: [TagTypes.Site],
    }),
    getOrganizationSites: builder.query<SiteWithAddress[], SiteListArgs>({
      providesTags: [TagTypes.Site],
      query: arg => {
        return {
          params: {
            limit: arg?.limit ?? 500,
            offset: arg?.offset ?? 0,
            ...(arg?.employeeSites && { employee_sites: arg.employeeSites }),
          },
          url: `/v1/organizations/${arg.organizationId}/sites`,
        };
      },
      transformResponse: (response: { results: SiteWithAddressDto[] }) => {
        return response?.results?.map(site => {
          return transformSiteWithAddress(site);
        });
      },
    }),
    getGlobalSites: builder.query<SiteWithAddress[], void>({
      providesTags: [TagTypes.GlobalSite],
      query: () => {
        return {
          url: `/v1/sites`,
        };
      },
      transformResponse: (response: SiteWithAddressDto[]) => {
        return response.map(site => {
          return transformSiteWithAddress(site);
        });
      },
    }),
    getSiteById: builder.query<
      SiteWithAddress,
      { siteId: string; organizationId: string }
    >({
      providesTags: [TagTypes.GlobalSite],
      query: ({ siteId, organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/sites/${siteId}`,
        };
      },
      transformResponse: (response: SiteWithAddressDto) => {
        return transformSiteWithAddress(response);
      },
    }),
    archiveSite: builder.mutation<
      void,
      { organizationId: string; siteId: string }
    >({
      query: ({ organizationId, siteId }) => ({
        url: `/v1/organizations/${organizationId}/sites/${siteId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: [TagTypes.Site, TagTypes.GlobalSite],
    }),
    //
    // Locations
    //
    getSiteLocations: builder.query<Location[], string>({
      providesTags: [TagTypes.Location],
      query: siteId => {
        return {
          url: `/v1/sites/${siteId}/locations`,
        };
      },
      transformResponse: (response: LocationDto[]) => {
        return response.map(location => {
          return transformLocation(location);
        });
      },
    }),
    createLocation: builder.mutation<Location, LocationCreationArgs>({
      query: ({ siteId, name }) => {
        return {
          url: `/v1/sites/${siteId}/locations`,
          method: 'POST',
          body: {
            name,
          },
        };
      },
      invalidatesTags: [TagTypes.Location],
    }),
    addFilesToAsset: builder.mutation<AddFilesResponseDto, AddFileToAssetArgs>({
      query: ({ organizationId, assetId, formData }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/images`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: AddFilesResponseDto) => ({
        message: response.message,
        uploaded_files: response.uploaded_files,
        errors: response.errors,
      }),
      invalidatesTags: (_result, _error, { assetId }) => [
        { type: TagTypes.AssetImage, id: assetId },
      ],
    }),
    generatePresignedUploadUrls: builder.mutation<
      PresignedUploadUrlResponse,
      GeneratePresignedUploadUrlsArgs
    >({
      query: ({
        organizationId,
        assetId,
        fileSamples,
        fileNames,
        fileSizes,
        contentTypes,
      }) => {
        const formData = new FormData();
        fileSamples.forEach(sample => {
          formData.append('file_samples', sample);
        });
        fileNames.forEach(name => {
          formData.append('file_names', name);
        });
        fileSizes.forEach(size => {
          formData.append('file_sizes', size.toString());
        });
        contentTypes.forEach(type => {
          formData.append('content_types', type);
        });

        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/images/presigned-urls`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (
        response: PresignedUploadUrlRawResponse
      ): PresignedUploadUrlResponse => ({
        message: response.message,
        presignedUrls: response.presigned_urls.map(url => ({
          filename: url.filename,
          presignedUrl: url.presigned_url,
          fileId: url.file_id,
          s3Key: url.s3_key,
          expiresIn: url.expires_in,
        })),
        errors: response.errors,
      }),
    }),
    getAssetImages: builder.query<
      AssetFileWithURL[],
      { organizationId: string; assetId: string }
    >({
      query: ({ organizationId, assetId }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/images`,
        };
      },
      transformResponse: (
        response: PaginatedAssetFileWithURLDto
      ): AssetFileWithURL[] => {
        return response.results.map(file => {
          return {
            fileId: file.file_id,
            url: file.url,
            fileName: file.file_name,
            assetId: file.asset_id,
            fileSizeBytes: file.file_size_bytes,
            fileType: file.file_type,
            uploadStatus: file.upload_status,
            createdAt: file.created_at,
            updatedAt: file.updated_at,
          };
        });
      },
      providesTags: (_result, _error, { assetId }) => [
        { type: TagTypes.AssetImage, id: assetId },
      ],
    }),
    getAssetImage: builder.query<
      AssetFileWithURL,
      { organizationId: string; assetId: string; assetFileId: string }
    >({
      query: ({ organizationId, assetId, assetFileId }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/images/${assetFileId}`,
        };
      },
      transformResponse: (response: AssetFileWithURLDto): AssetFileWithURL => {
        return {
          fileId: response.file_id,
          url: response.url,
          fileName: response.file_name,
          assetId: response.asset_id,
          fileSizeBytes: response.file_size_bytes,
          fileType: response.file_type,
          uploadStatus: response.upload_status,
          createdAt: response.created_at,
          updatedAt: response.updated_at,
        };
      },
      providesTags: (_result, _error, { assetFileId }) => [
        { type: TagTypes.AssetImage, id: assetFileId },
      ],
    }),
    deleteAssetImage: builder.mutation<
      void,
      { organizationId: string; assetId: string; assetFileId: string }
    >({
      query: ({ organizationId, assetId, assetFileId }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/images/${assetFileId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, { assetId, assetFileId }) => [
        { type: TagTypes.AssetImage, id: assetId },
        { type: TagTypes.AssetImage, id: assetFileId },
      ],
    }),
    getWarehouseMetrics: builder.query<
      WarehouseMetrics,
      {
        organizationId: string;
      }
    >({
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/assets/warehousing/metrics`,
        };
      },
      transformResponse: (response: WarehouseMetricsDto): WarehouseMetrics => {
        return {
          assetCounts: [
            {
              month: 'previousMonth',
              count: response.asset_count.previous_month,
            },
            {
              month: 'currentMonth',
              count: response.asset_count.current_month,
            },
          ],
        };
      },
    }),
    getAssetEventsHistory: builder.query<
      AssetEventsHistoryResponse,
      AssetEventsHistoryArgs
    >({
      query: ({
        organizationId,
        assetId,
        pageCursorDate,
        actionTypes,
        categories,
        sortOrder,
      }) => {
        const params = new URLSearchParams();

        pageCursorDate &&
          params.append('page_cursor_date', String(pageCursorDate));
        sortOrder && params.append('sort_order', String(sortOrder));

        // Adding the list parameter
        actionTypes &&
          actionTypes.forEach(action => params.append('action_types', action));
        categories &&
          categories.forEach(category => params.append('category', category));

        return {
          url: `/v1/organizations/${organizationId}/assets/${assetId}/events?${params.toString()}`,
        };
      },
      transformResponse: (response: AssetEventsResponseDto) => {
        return {
          assetEvents: response.events.map(event => ({
            action: event.action,
            date: event.date,
            actionType: event.action_type,
            category: event.category,
            source: event.source,
            performedBy: event.performed_by,
            extraInfo: event.extra_info as Record<string, unknown>,
          })),
          lastDate: response.last_date ?? '',
        };
      },
      providesTags: [TagTypes.AssetEvents],
    }),
    downloadAllAssetEvents: builder.query<
      AssetEvent[],
      { organizationId: string; assetId: string }
    >({
      queryFn: async (
        { organizationId, assetId },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const allEvents: AssetEvent[] = [];
        let pageCursorDate: string | undefined = undefined;

        do {
          const params = new URLSearchParams();
          if (pageCursorDate) {
            params.append('page_cursor_date', pageCursorDate);
          }
          params.append('sort_order', SortOrder.Desc);

          const result = await baseQuery({
            url: `/v1/organizations/${organizationId}/assets/${assetId}/events?${params.toString()}`,
          });

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data as AssetEventsResponseDto;
          const events: AssetEvent[] = response.events.map(event => ({
            action: event.action,
            date: event.date,
            actionType: event.action_type,
            category: event.category,
            source: event.source,
            performedBy: event.performed_by,
            extraInfo: event.extra_info as Record<string, unknown>,
          }));

          if (events.length === 0) break;

          allEvents.push(...events);
          pageCursorDate = response.last_date || undefined;
        } while (pageCursorDate);

        return { data: allEvents };
      },
    }),
    /**
     * The earliest recorded LOGIN_ATTEMPT across the given devices, or null if
     * none of them has one. Used by the employee timeline's "first device login".
     */
    getFirstDeviceLoginDate: builder.query<
      string | null,
      { organizationId: string; assetIds: string[] }
    >({
      queryFn: async (
        { organizationId, assetIds },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const results = await Promise.all(
          assetIds.map(assetId => {
            const params = new URLSearchParams();
            params.append('sort_order', SortOrder.Asc);
            params.append(
              'action_types',
              DirectoryInsightEventType.LoginAttempt
            );
            return baseQuery({
              url: `/v1/organizations/${organizationId}/assets/${assetId}/events?${params.toString()}`,
            });
          })
        );

        const failed = results.find(result => result.error);
        if (failed?.error) {
          return { error: failed.error };
        }

        const dates = results.flatMap(result =>
          (result.data as AssetEventsResponseDto).events
            // Filtered server-side too; re-checked so an ignored filter can't
            // surface an unrelated event as a login.
            .filter(
              event =>
                event.action_type === DirectoryInsightEventType.LoginAttempt
            )
            .map(event => event.date)
        );

        if (dates.length === 0) return { data: null };

        return {
          data: dates.reduce((earliest, date) =>
            new Date(date) < new Date(earliest) ? date : earliest
          ),
        };
      },
      providesTags: [TagTypes.AssetEvents],
    }),
  }),
});

const transformSiteWithAddress = (
  site: SiteWithAddressDto
): SiteWithAddress => {
  // TODO: The DTO type from @electricjs/core_entity-client does not yet include
  // archived_at / is_archivable. Remove this workaround once the client package is updated.
  const siteRecord = site as unknown as Record<string, unknown>;

  return {
    id: site.id,
    name: site.name,
    phone: site.phone_number,
    address: {
      streetAddress1: site.address.street_1,
      streetAddress2: site.address.street_2,
      city: site.address.city,
      state: site.address.state,
      zip: site.address.zip_code ?? '',
      country: site.address.country,
    },
    organizationId: site.organization_id || null,
    archivedAt: (siteRecord.archived_at as string | null) ?? null,
    isArchivable: (siteRecord.is_archivable as boolean) ?? false,
  };
};

const transformShipmentAssetEmployeeAddress = (
  address: ShipmentAssetEmployeeAddressDto
): Address => ({
  streetAddress1: address.street_1,
  streetAddress2: address.street_2,
  city: address.city,
  state: address.state,
  zip: address.zip_code ?? '',
  country: address.country,
});

const transformShipmentAssetEmployee = (
  employee: ShipmentAssetEmployeeDto
): ShipmentEligibleAssetEmployee => {
  const { address, ...rest } = employee;

  return {
    ...camelize(rest),
    address: address
      ? transformShipmentAssetEmployeeAddress(address)
      : undefined,
  } as ShipmentEligibleAssetEmployee;
};

const transformShipmentEligibleAsset = (
  asset: ShipmentAssetDto
): ShipmentEligibleAsset => {
  const { site, assigned_to_employee, ...rest } = asset;

  return {
    ...camelize(rest),
    site: site ? transformSiteWithAddress(site) : null,
    assignedToEmployee: assigned_to_employee
      ? transformShipmentAssetEmployee(assigned_to_employee)
      : undefined,
  } as ShipmentEligibleAsset;
};

const transformStorefrontAsset = (
  asset: StorefrontAssetDto
): StorefrontAsset => {
  const { site, is_shippable, self_ship_required, ...rest } = asset;

  return {
    ...camelize(rest),
    site: site ? transformSiteWithAddress(site) : null,
    isShippable: is_shippable,
    selfShipRequired: self_ship_required,
  } as StorefrontAsset;
};

const transformDate = (date: Date | undefined | null) => {
  if (date) {
    return isValid(date) ? date.toISOString() : undefined;
  }
  return date;
};

const transformLocation = (location: LocationDto): Location => {
  return {
    id: location.id,
    name: location.name,
    siteId: location.site_id,
  };
};

// export auto-generated hooks
export const {
  useCreateAssetsMutation,
  useUpdateAssetMutation,
  useGetAssetQuery,
  useGetBulkAssetsQuery,
  useLazyGetBulkAssetsQuery,
  useCreateSiteMutation,
  useGetOrganizationSitesQuery,
  useLazyGetOrganizationSitesQuery,
  useListAssetsV2Query,
  useLazyListAssetsV2Query,
  useSearchAssetsEligibleForShipmentQuery,
  useLazySearchAssetsEligibleForShipmentQuery,
  useGetAssetHistoryQuery,
  useGetMyAssetsQuery,
  useGetEmployeeAssetsQuery,
  useGetOrganizationAssetHistoryQuery,
  useGetOrganizationAssetHistoryUnresolvedQuery,
  useIgnoreAssetLogMutation,
  useLazyGetAssetQuery,
  useLazyListAssetsStorefrontQuery,
  useLazyGetAssetStorefrontQuery,
  useGetGlobalSitesQuery,
  useGetSiteByIdQuery,
  useArchiveSiteMutation,
  useGetSiteLocationsQuery,
  useCreateLocationMutation,
  useUpdateAssetLocationMutation,
  useAddFilesToAssetMutation,
  useGeneratePresignedUploadUrlsMutation,
  useGetAssetImagesQuery,
  useGetAssetImageQuery,
  useDeleteAssetImageMutation,
  useDeleteAssetMutation,
  useGetWarehouseMetricsQuery,
  useLazyGetAssetEventsHistoryQuery,
  useGetFirstDeviceLoginDateQuery,
  useLazyDownloadAllAssetEventsQuery,
} = assetApiSlice;
